/**
 * Documentation Viewer Page
 * Displays training documentation with print, share, and PDF export capabilities
 * Supports Mermaid diagrams for visual flowcharts
 * 
 * Refactored: Documentation content moved to /src/data/docs/ for maintainability
 */

import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import mermaid from 'mermaid';
import html2pdf from 'html2pdf.js';
import { 
  Printer, 
  Share2, 
  Check, 
  ChevronLeft,
  Book,
  Users,
  ShoppingCart,
  Package,
  Settings,
  Menu,
  DollarSign,
  Download,
  Loader2,
  Cpu
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

// Import documentation content from separate modules
import { DOCS } from '../../data/docs/docsIndex';

// Initialize Mermaid with custom theme
mermaid.initialize({
  startOnLoad: false,
  theme: 'base',
  themeVariables: {
    primaryColor: '#8B7355',
    primaryTextColor: '#333',
    primaryBorderColor: '#6B5B4A',
    lineColor: '#8B7355',
    secondaryColor: '#f5f5f0',
    tertiaryColor: '#fff',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  flowchart: {
    curve: 'basis',
    padding: 20,
  },
});

// Mermaid Diagram Component
const MermaidDiagram = ({ chart }) => {
  const containerRef = useRef(null);
  const [svg, setSvg] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    const renderDiagram = async () => {
      if (!chart || !containerRef.current) return;
      
      try {
        const id = `mermaid-${Math.random().toString(36).substr(2, 9)}`;
        const { svg } = await mermaid.render(id, chart);
        setSvg(svg);
        setError(null);
      } catch (err) {
        console.error('Mermaid render error:', err);
        setError(err.message);
      }
    };

    renderDiagram();
  }, [chart]);

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 my-4 text-sm text-red-700">
        <strong>Diagram Error:</strong> {error}
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className="mermaid-container my-6 flex justify-center overflow-x-auto"
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
};

// Custom code block renderer for Mermaid support
const CodeBlock = ({ className, children, ...props }) => {
  const match = /language-(\w+)/.exec(className || '');
  const language = match ? match[1] : '';
  
  if (language === 'mermaid') {
    return <MermaidDiagram chart={String(children).replace(/\n$/, '')} />;
  }
  
  return (
    <code className={className} {...props}>
      {children}
    </code>
  );
};

// Icon mapping for documentation types
const iconMap = {
  'index': Book,
  'customer': Users,
  'order': ShoppingCart,
  'purchase-order': Package,
  'product-catalog': Package,
  'payments': DollarSign,
  'settings': Settings,
  'tech-stack': Cpu,
};

const DocsViewer = () => {
  const [activeDoc, setActiveDoc] = useState('index');
  const [copied, setCopied] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const contentRef = useRef(null);

  const docs = [
    { id: 'index', title: 'Overview', icon: Book },
    { id: 'customer', title: 'Customer Module', icon: Users },
    { id: 'order', title: 'Order Module', icon: ShoppingCart },
    { id: 'purchase-order', title: 'Purchase Orders', icon: Package },
    { id: 'product-catalog', title: 'Product Catalog (Detailed)', icon: Package },
    { id: 'payments', title: 'Payments', icon: DollarSign },
    { id: 'settings', title: 'Admin Settings', icon: Settings },
    { id: 'tech-stack', title: 'Tech Stack', icon: Cpu },
  ];

  // PDF Export function
  const handleExportPDF = async () => {
    if (!contentRef.current) return;
    
    setIsExporting(true);
    toast.info('Preparing PDF export...');
    
    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const wrapper = document.createElement('div');
      wrapper.style.cssText = 'position: absolute; left: -9999px; top: 0; width: 800px; background: white;';
      
      const header = document.createElement('div');
      header.innerHTML = `
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 30px; padding: 20px 40px 20px 40px; border-bottom: 2px solid #8B7355;">
          <div style="font-size: 28px; font-weight: bold; color: #8B7355;">Engraveca</div>
          <div style="color: #666; font-size: 12px; text-align: right;">
            <div style="font-weight: 600;">${DOCS[activeDoc].title}</div>
            <div>Generated: ${new Date().toLocaleDateString()}</div>
          </div>
        </div>
      `;
      wrapper.appendChild(header);
      
      const contentClone = contentRef.current.cloneNode(true);
      contentClone.style.padding = '0 40px 40px 40px';
      wrapper.appendChild(contentClone);
      
      const styleEl = document.createElement('style');
      styleEl.textContent = `
        * { box-sizing: border-box; }
        h1 { color: #8B7355 !important; border-bottom: 2px solid #8B7355; padding-bottom: 10px; margin-top: 25px; font-size: 24px; }
        h2 { color: #5D4E3C !important; margin-top: 20px; font-size: 18px; }
        h3 { color: #6B5B4A !important; margin-top: 15px; font-size: 14px; }
        p { margin: 10px 0; line-height: 1.6; font-size: 12px; }
        table { border-collapse: collapse; width: 100%; margin: 15px 0; }
        th, td { border: 1px solid #ddd; padding: 8px 10px; text-align: left; font-size: 11px; }
        th { background: #f5f5f0 !important; font-weight: 600; }
        code { background: #f5f5f0; padding: 2px 6px; border-radius: 3px; font-size: 10px; }
        pre { background: #f5f5f0; padding: 12px; border-radius: 5px; overflow-x: auto; font-size: 10px; white-space: pre-wrap; }
        ul, ol { margin: 10px 0; padding-left: 25px; font-size: 12px; }
        li { margin: 5px 0; }
        hr { border: none; border-top: 1px solid #ddd; margin: 20px 0; }
        svg { max-width: 100% !important; height: auto !important; }
        .mermaid-container { margin: 15px 0; text-align: center; }
      `;
      wrapper.appendChild(styleEl);
      
      document.body.appendChild(wrapper);
      
      const opt = {
        margin: [10, 10, 15, 10],
        filename: `${DOCS[activeDoc].title.replace(/[^a-zA-Z0-9]/g, '_')}_Training_Guide.pdf`,
        image: { type: 'jpeg', quality: 0.95 },
        html2canvas: { 
          scale: 2,
          useCORS: true,
          logging: false,
          width: 800,
          windowWidth: 800,
        },
        jsPDF: { 
          unit: 'mm', 
          format: 'a4', 
          orientation: 'portrait' 
        },
        pagebreak: { mode: ['css', 'legacy'], avoid: ['tr', 'td', 'svg'] }
      };
      
      await html2pdf().set(opt).from(wrapper).save();
      document.body.removeChild(wrapper);
      
      toast.success('PDF downloaded successfully!');
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('Failed to export PDF. Please try the Print option instead.');
    } finally {
      setIsExporting(false);
    }
  };

  const handlePrint = () => {
    const printContent = contentRef.current;
    if (!printContent) return;
    
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Please allow popups to print');
      return;
    }
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>${DOCS[activeDoc].title} - Engraveca Training</title>
        <style>
          * { box-sizing: border-box; }
          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            line-height: 1.6;
            max-width: 800px;
            margin: 0 auto;
            padding: 40px 20px;
            color: #333;
          }
          h1 { color: #8B7355; border-bottom: 2px solid #8B7355; padding-bottom: 10px; font-size: 24px; }
          h2 { color: #5D4E3C; margin-top: 30px; font-size: 18px; }
          h3 { color: #6B5B4A; font-size: 14px; }
          table { border-collapse: collapse; width: 100%; margin: 15px 0; }
          th, td { border: 1px solid #ddd; padding: 10px; text-align: left; font-size: 11px; }
          th { background: #f5f5f0; font-weight: 600; }
          code { background: #f5f5f0; padding: 2px 6px; border-radius: 3px; font-size: 0.9em; }
          pre { background: #f5f5f0; padding: 15px; border-radius: 5px; overflow-x: auto; white-space: pre-wrap; }
          blockquote { border-left: 4px solid #8B7355; margin: 15px 0; padding-left: 15px; color: #666; }
          hr { border: none; border-top: 1px solid #ddd; margin: 30px 0; }
          .header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; padding-bottom: 15px; border-bottom: 2px solid #8B7355; }
          .logo { font-size: 24px; font-weight: bold; color: #8B7355; }
          .date { color: #888; font-size: 12px; }
          svg { max-width: 100% !important; height: auto !important; display: block; margin: 15px auto; }
          .mermaid-container { margin: 20px 0; text-align: center; page-break-inside: avoid; }
          @media print {
            body { padding: 20px; }
            h1, h2 { break-after: avoid; }
            table, svg, .mermaid-container { break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="logo">Engraveca</div>
          <div class="date">
            <div style="font-weight: 600;">${DOCS[activeDoc].title}</div>
            <div>Printed: ${new Date().toLocaleDateString()}</div>
          </div>
        </div>
        ${printContent.innerHTML}
      </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  const handleShare = async () => {
    const shareUrl = `${window.location.origin}/admin/docs?doc=${activeDoc}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${DOCS[activeDoc].title} - Engraveca Training`,
          text: 'Check out this training documentation',
          url: shareUrl,
        });
      } catch (err) {
        handleCopyLink(shareUrl);
      }
    } else {
      handleCopyLink(shareUrl);
    }
  };

  const handleCopyLink = (url) => {
    navigator.clipboard.writeText(url || `${window.location.origin}/admin/docs?doc=${activeDoc}`);
    setCopied(true);
    toast.success('Link copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  // Parse URL params on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const docParam = params.get('doc');
    if (docParam && DOCS[docParam]) {
      setActiveDoc(docParam);
    }
  }, []);

  const currentDoc = DOCS[activeDoc];
  const DocIcon = iconMap[activeDoc] || Book;

  return (
    <div className="flex h-[calc(100vh-120px)] bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'w-64' : 'w-0'} transition-all duration-300 bg-gradient-to-b from-stone-50 to-stone-100 border-r border-gray-200 overflow-hidden`}>
        <div className="p-4">
          <div className="flex items-center gap-2 mb-6">
            <Book className="w-6 h-6 text-amber-700" />
            <h2 className="font-bold text-gray-800">Training Docs</h2>
          </div>
          
          <nav className="space-y-1">
            {docs.map((doc) => {
              const Icon = doc.icon;
              const isActive = activeDoc === doc.id;
              return (
                <button
                  key={doc.id}
                  onClick={() => setActiveDoc(doc.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all ${
                    isActive 
                      ? 'bg-amber-100 text-amber-900 font-medium shadow-sm' 
                      : 'text-gray-600 hover:bg-white hover:text-gray-900'
                  }`}
                >
                  <Icon className={`w-4 h-4 ${isActive ? 'text-amber-700' : 'text-gray-400'}`} />
                  <span className="text-sm">{doc.title}</span>
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden"
            >
              <Menu className="w-5 h-5" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="hidden lg:flex"
            >
              <ChevronLeft className={`w-5 h-5 transition-transform ${!sidebarOpen ? 'rotate-180' : ''}`} />
            </Button>
            <div className="flex items-center gap-2">
              <DocIcon className="w-5 h-5 text-amber-700" />
              <h1 className="text-lg font-semibold text-gray-800">{currentDoc.title}</h1>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleShare}
              className="gap-2"
              data-testid="share-doc-btn"
            >
              {copied ? <Check className="w-4 h-4 text-green-600" /> : <Share2 className="w-4 h-4" />}
              {copied ? 'Copied!' : 'Share'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportPDF}
              disabled={isExporting}
              className="gap-2"
              data-testid="export-pdf-btn"
            >
              {isExporting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              {isExporting ? 'Exporting...' : 'PDF'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrint}
              className="gap-2"
              data-testid="print-doc-btn"
            >
              <Printer className="w-4 h-4" />
              Print
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto">
          <div 
            ref={contentRef}
            className="max-w-4xl mx-auto px-8 py-6 prose prose-stone prose-headings:text-stone-800 prose-h1:text-amber-800 prose-h1:border-b prose-h1:border-amber-200 prose-h1:pb-3 prose-h2:text-stone-700 prose-a:text-amber-700 prose-strong:text-stone-700 prose-code:bg-stone-100 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-amber-800 prose-code:before:content-none prose-code:after:content-none prose-pre:bg-stone-100 prose-pre:border prose-pre:border-stone-200 prose-table:border-collapse prose-th:bg-stone-100 prose-th:border prose-th:border-stone-300 prose-th:px-3 prose-th:py-2 prose-td:border prose-td:border-stone-200 prose-td:px-3 prose-td:py-2"
          >
            <ReactMarkdown 
              remarkPlugins={[remarkGfm]}
              components={{
                code: CodeBlock,
              }}
            >
              {currentDoc.content}
            </ReactMarkdown>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocsViewer;
